import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import useAutocomplete from "@mui/material/useAutocomplete";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import TextField from "@mui/material/TextField";
import COLOR from "lib/colors";
import _debounce from "lodash/debounce";
import {
    ArrowDropUpIcon,
    ArrowDropDownIcon,
    CheckBoxCheckedIcon,
    CheckBoxIcon,
    CheckIcon,
    CloseIcon,
    SearchIcon,
} from "common/DS/Icons";
import { Caption } from "common/DS/Text";
import classNames from "classnames";
import { FilterGroupContext } from "common/DS/FilterToolbar";
import { isNullOrUndefined } from "lib/util";
import { useHasPermanentScrollbar } from "lib/hooks/window";
import { TextButton } from "./Buttons";

const MIN_LIST_COUNT = 10;

const filterButtonOptionType = PropTypes.oneOfType([
    PropTypes.shape({
        meta: PropTypes.node,
        text: PropTypes.string,
        value: PropTypes.node,
        hidden: PropTypes.bool,
    }),
    PropTypes.shape({
        node: PropTypes.node,
        value: PropTypes.func,
        hidden: PropTypes.bool,
    }),
]);

const FilterAutoComplete = ({
    multi,
    selected,
    onSelect,
    options,
    onClear,
    showMultiSelectClear,
    onOnlySelect,
    disabled,
}) => {
    const {
        getRootProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        getClearProps,
        groupedOptions,
        inputValue,
    } = useAutocomplete({
        // MUI uses this ID for the listbox & options
        id: "ws-filter-button-autocomplete_input",
        options,
        getOptionLabel: (option) => option.text,
        open: true,
        autoComplete: true,
        multiple: true,
        clearOnBlur: false,
        isOptionEqualToValue: (opt, selectedOpt) => opt.value === selectedOpt.value,
    });

    return (
        <div style={{ maxWidth: "270px" }}>
            <div {...getRootProps()}>
                <TextField
                    placeholder="Search"
                    className="flex"
                    InputProps={{
                        className: "ws-filter-button__input",
                        startAdornment: <SearchIcon style={{ fill: COLOR["navy-medium"] }} />,
                        endAdornment: inputValue.length > 0 && (
                            <IconButton {...getClearProps()}>
                                <CloseIcon />
                            </IconButton>
                        ),
                    }}
                    // inputProps and InputProps are not the same in MUI world
                    // need this to silence MUI error "Unable to find input element"
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                    inputProps={{ ...getInputProps() }}
                />
            </div>
            {groupedOptions.length > 0 ? (
                <ul className="ws-filter-button__options" {...getListboxProps()}>
                    {groupedOptions.map((option, index) => (
                        <MenuItem
                            className="ws-filter-button__single_option"
                            data-value={option.value}
                            data-test="filter-button-item"
                            {...getOptionProps({ option, index })}
                            onClick={(event) => onSelect(event, option.value)}
                            disabled={disabled}
                        >
                            {multi && (
                                <ListItemIcon onClick={(event) => onSelect(event, option.value)}>
                                    {selected.includes(option.value) ? (
                                        <CheckBoxCheckedIcon style={{ color: COLOR.indigo }} />
                                    ) : (
                                        <CheckBoxIcon />
                                    )}
                                </ListItemIcon>
                            )}
                            <ListItemText onClick={(event) => onSelect(event, option.value)}>
                                {option.text}
                            </ListItemText>
                            {option.meta && (
                                <Caption className="ws-filter-menu__item">{option.meta}</Caption>
                            )}
                            {selected.length >= 2 && selected.includes(option.value) && (
                                <TextButton
                                    className="ws-filter-button-autocomplete__only_btn"
                                    onClick={(event) => {
                                        onOnlySelect(event, option.value);
                                        event.stopPropagation();
                                    }}
                                >
                                    ONLY
                                </TextButton>
                            )}
                        </MenuItem>
                    ))}
                </ul>
            ) : (
                <div className="ws-filter-button-autocomplete__no_results"> No results 🤔</div>
            )}
            {showMultiSelectClear && (
                <div className="ws-filter-button-autocomplete__clear">
                    <TextButton className="ws-filter-button__clear" onClick={onClear}>
                        Clear
                    </TextButton>
                </div>
            )}
        </div>
    );
};

FilterAutoComplete.propTypes = {
    multi: PropTypes.bool,
    selected: PropTypes.array,
    onSelect: PropTypes.func.isRequired,
    options: PropTypes.array,
    onClear: PropTypes.func.isRequired,
    showMultiSelectClear: PropTypes.bool,
    onOnlySelect: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

FilterAutoComplete.defaultProps = {
    multi: false,
    selected: [],
    options: [],
    showMultiSelectClear: false,
    disabled: false,
};

const FilterMenuItem = ({ multi, option, selected, onSelect, disabled }) => (
    <MenuItem
        className="ws-filter-menu__item"
        key={option.value}
        onClick={(event) => onSelect(event, option.value)}
        data-value={option.value}
        data-test="filter-button-item"
        disabled={disabled}
    >
        {multi && (
            <ListItemIcon>
                {selected.includes(option.value) ? <CheckBoxCheckedIcon /> : <CheckBoxIcon />}
            </ListItemIcon>
        )}
        <ListItemText>{option.text}</ListItemText>
        {option.meta && <Caption className="ws-filter-menu__item-meta">{option.meta}</Caption>}
    </MenuItem>
);
FilterMenuItem.propTypes = {
    multi: PropTypes.bool.isRequired,
    option: filterButtonOptionType.isRequired,
    selected: PropTypes.arrayOf(PropTypes.node).isRequired,
    onSelect: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

FilterMenuItem.defaultProps = {
    disabled: false,
};

const FilterNodeMenuItem = ({ option, onSelect }) =>
    React.cloneElement(option.node, { handleFilterSelect: onSelect });
FilterNodeMenuItem.propTypes = {
    option: filterButtonOptionType.isRequired,
    onSelect: PropTypes.func.isRequired,
};

const FilterNonAutocomplete = ({
    options,
    onSelect,
    showMultiSelectClear,
    onClear,
    multi,
    selected,
    showSingleSelectClear,
    disabled,
}) => (
    <>
        {showSingleSelectClear && (
            <MenuItem className="ws-menu-item__link" onClick={onClear}>
                Clear
            </MenuItem>
        )}

        {options.map((option) => {
            const props = {
                key: option.value,
                option,
                selected,
                onSelect,
                multi,
                disabled,
            };

            return option.node ? <FilterNodeMenuItem {...props} /> : <FilterMenuItem {...props} />;
        })}

        {showMultiSelectClear && (
            <div className="ws-filter-button-autocomplete__clear">
                <TextButton onClick={onClear}>Clear</TextButton>
            </div>
        )}
    </>
);

FilterNonAutocomplete.propTypes = {
    options: PropTypes.array,
    onSelect: PropTypes.func.isRequired,
    showMultiSelectClear: PropTypes.bool,
    onClear: PropTypes.func.isRequired,
    multi: PropTypes.bool,
    selected: PropTypes.array,
    showSingleSelectClear: PropTypes.bool,
    disabled: PropTypes.bool,
};

FilterNonAutocomplete.defaultProps = {
    multi: false,
    selected: [],
    options: [],
    showMultiSelectClear: false,
    showSingleSelectClear: true,
    disabled: false,
};

function FilterButton({
    defaultEmptyOption, // Default option used if the filter is cleared or otherwise empty
    defaultOption, // Default option to use when mounted
    endIcon, // Button's endIcon
    handleChange, // callback for when the filter value changes
    handleOpen,
    label, // Text label for the filter (visible when empty)
    multi, // Use multi-select
    options, // Array of options for the select
    showArrowIcon, // Show an arrow icon at the end of the button
    showClearIcon, // Show a clear icon at the end of the button when a value is selected
    showClearOption, // Show a clear option at the top of the select menu
    startIcon, // Button's startIcon
    toggle, // Use a toggle switch filter (one value, selected or not)
    autocomplete, // Show autocomplete input
    isOpen: open,
    className,
    menuProps,
    debounceMs,
    disabled,
    ...props // Props for MUI Button
}) {
    if (toggle && multi) {
        throw new Error("Cannot use toggle and multi together");
    }

    const { registerClearHandler, inDialog } = useContext(FilterGroupContext);

    const single = !multi;
    const button = useRef();

    const [anchorEl, setAnchorEl] = useState(null);
    const [isOpen, setIsOpen] = useState(open);
    const hasScrollbar = useHasPermanentScrollbar();

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    useEffect(() => {
        if (handleOpen) {
            handleOpen(isOpen);
        }
    }, [isOpen, handleOpen]);

    const emptyOption = useMemo(() => {
        if (Array.isArray(defaultEmptyOption)) {
            return defaultEmptyOption;
        }

        return isNullOrUndefined(defaultEmptyOption) ? [] : [defaultEmptyOption];
    }, [defaultEmptyOption]);

    const [selected, setSelected] = useState(() => {
        if (Array.isArray(defaultOption)) {
            return defaultOption;
        }

        return isNullOrUndefined(defaultOption) ? emptyOption : [defaultOption];
    });

    const debounceChange = useMemo(() => _debounce((value) => handleChange(value), debounceMs), []);

    function sendHandleChange(value) {
        if (!handleChange) {
            return;
        }

        if (multi) {
            debounceChange(value);
        } else {
            debounceChange(value.length === 0 ? null : value[0]);
        }
    }

    function updateSelected(value) {
        // If value is empty and we have an empty option we need to use it as the updated value
        const newValue = emptyOption.length > 0 && value.length === 0 ? emptyOption : value;
        setSelected(newValue);
        sendHandleChange(newValue);
    }

    function clearSelected() {
        updateSelected(emptyOption);
    }

    useEffect(() => {
        if (!registerClearHandler) {
            return () => {};
        }

        registerClearHandler(label, clearSelected);
        return () => registerClearHandler(label, clearSelected, true);
    }, []);

    // Force-remove focus whenever stuff changes
    useEffect(() => {
        if (button.current) {
            button.current.blur();
        }
    }, [isOpen, selected]);

    function closeMenu() {
        setAnchorEl(null);
        setIsOpen(false);
    }

    function onClear(event) {
        event.preventDefault();
        clearSelected();
    }

    function updateSelectedValue(value) {
        const exists = selected.includes(value);

        let newSelected = selected.slice();
        if (exists && (multi || toggle)) {
            newSelected = newSelected.filter((v) => v !== value);
        } else if (multi) {
            newSelected.push(value);
        } else {
            newSelected = [value];
        }
        updateSelected(newSelected);
    }

    function onSelect(event, value) {
        event.preventDefault();

        updateSelectedValue(value);

        if (single) {
            closeMenu();
        }
    }

    function onToggleMenu(event) {
        if (toggle) {
            updateSelectedValue(options[0].value);
            return;
        }

        if (isOpen) {
            closeMenu();
        } else {
            setAnchorEl(event.currentTarget);
            setIsOpen(true);
        }
    }
    function onOnlySelect(event, value) {
        event.preventDefault();

        const exists = selected.includes(value);

        if (exists) {
            // use updateSelected to make sure we still fire off all the handlers
            updateSelected([value]);
        }
    }

    let hasValue = selected.length > 0;
    const hasIconLabel = typeof label !== "string";

    let labelText = "";
    let labelCountText = "";

    if (!hasIconLabel) {
        labelText = label;

        if (toggle) {
            labelText = options[0].text;
        } else if (hasValue) {
            const option = options.find((o) => o.value === selected[0]);

            if (option) {
                if (option.getLabelText) {
                    labelText = option.getLabelText();
                } else {
                    labelText = option.text;
                    if (selected.length > 1) {
                        labelCountText = `+${selected.length - 1}`;
                    }
                }
            }
        }
    }

    // Bit of a mouthful here. In a case where we have a default empty option and the only
    // selected value is the default option, we show the text of the option in the label
    // (hence why this conditional is down here and not above), but we don't count it as
    // "selected" or show the filter as "active"
    // An example is the Retain Date Range filter, where we default to 180 days. We don't
    // want to show the filter as "active" if 180 is selected, since that's the default
    // state for the filter. But the filter itself does have a value, which is 180!
    if (
        defaultEmptyOption !== undefined && // defaultEmptyOption might be falsey so test defined
        selected.length === 1 &&
        selected[0] === defaultEmptyOption
    ) {
        hasValue = false;
    }

    const hasClearIcon = showClearIcon && hasValue;
    const hasToggleIcon = toggle && hasValue;

    let buttonStartIcon = startIcon;
    if (hasToggleIcon) {
        buttonStartIcon = <CheckIcon />;
    } else if (hasIconLabel) {
        buttonStartIcon = label;
    }

    let buttonEndIcon = endIcon;
    if (hasClearIcon) {
        buttonEndIcon = null;
    } else if (showArrowIcon && !toggle) {
        buttonEndIcon = isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />;
    }

    const buttonClassName = classNames(
        "ws-filter-button__button",
        {
            "ws-filter-button__button--active": hasValue,
            "ws-filter-button__button--icon-only":
                hasIconLabel && !hasToggleIcon && !buttonEndIcon && !hasClearIcon,
            "ws-filter-button__button--label-icon": hasIconLabel,
            "ws-filter-button__button--start-icon": !!buttonStartIcon,
            "ws-filter-button__button--end-icon": !!buttonEndIcon,
            "ws-filter-button__button--clear-icon": hasClearIcon,
            "ws-filter-button__button--open": isOpen,
        },
        className,
    );

    const containerClassName = classNames("ws-filter-container", {
        "ws-filter-container--open": isOpen,
    });

    const visibleOptions = options.filter((o) => !o.hidden);
    const hasAutoCompleteSearchBox = visibleOptions.length >= MIN_LIST_COUNT && autocomplete;

    // So because it was 100000000000000x easier than a more proper implementation,
    // I hacked the "mobile" dialog view to just be a CSS re-stylization of these buttons
    // and menus. There are a couple things that doesn't quite work for, and one is MUI's
    // Menu component, which is always a popover that's inserted into the DOM at the end of
    // the body. Since we need those to show up inline when it's inside the dialog, this thing
    // from the toolbar's context will signal to use MenuList instead, which will display the
    // menu inline.
    const MenuComponent = inDialog ? MenuList : Menu;
    let finalMenuProps = {};
    if (inDialog) {
        finalMenuProps = {
            open: isOpen,
            onClose: closeMenu,
            className: "ws-filter-menu",
            ...menuProps,
        };
    } else {
        finalMenuProps = {
            anchorEl,
            open: isOpen,
            onClose: closeMenu,
            PaperProps: { className: "ws-filter-menu__container" },
            MenuListProps: {
                // fixes padding when the auto complete search is shown
                className: hasAutoCompleteSearchBox
                    ? "ws-filter-menu__autocomplete"
                    : "ws-filter-menu",
                disablePadding: true,
            },
            disableAutoFocusItem: true,
            ...menuProps,
        };
    }

    const showMultiSelectClear = showClearOption && multi && selected.length >= 2;
    const showSingleSelectClear = showClearOption && !multi && selected.length >= 1;

    if (!options.length) {
        return null;
    }

    return (
        <div className={containerClassName}>
            <div className="ws-filter-button dib prel">
                <Button
                    ref={button}
                    className={buttonClassName}
                    size="small"
                    onClick={onToggleMenu}
                    startIcon={buttonStartIcon}
                    endIcon={buttonEndIcon}
                    disabled={disabled}
                    {...props}
                >
                    {inDialog && (
                        <div className="flex flex-ac flex-jsb flex-grow">
                            <span className="ws-filter-button__title">{label}</span>
                            <div className="flex flex-ac">
                                {labelText && labelText !== label && (
                                    <span className="ws-filter-button__label">{labelText}</span>
                                )}
                                {labelCountText && (
                                    <span className="ws-filter-button__count">
                                        {labelCountText}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    {!inDialog && (
                        <>
                            <span className="ws-filter-button__label">{labelText}</span>
                            {labelCountText && (
                                <span className="ws-filter-button__count">{labelCountText}</span>
                            )}
                        </>
                    )}
                </Button>
                {hasClearIcon && (
                    <IconButton
                        className="ws-filter-button__clear-icon"
                        onClick={onClear}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </div>
            <MenuComponent disableScrollLock={hasScrollbar} {...finalMenuProps}>
                {anchorEl &&
                    (hasAutoCompleteSearchBox ? (
                        <FilterAutoComplete
                            multi={multi}
                            selected={selected}
                            onSelect={onSelect}
                            options={visibleOptions}
                            onClear={onClear}
                            showMultiSelectClear={showMultiSelectClear}
                            onOnlySelect={onOnlySelect}
                            disabled={disabled}
                        />
                    ) : (
                        <FilterNonAutocomplete
                            multi={multi}
                            selected={selected}
                            onSelect={onSelect}
                            options={visibleOptions}
                            onClear={onClear}
                            showMultiSelectClear={showMultiSelectClear}
                            showSingleSelectClear={showSingleSelectClear}
                            disabled={disabled}
                        />
                    ))}
            </MenuComponent>
        </div>
    );
}

FilterButton.propTypes = {
    className: PropTypes.string,
    defaultEmptyOption: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    defaultOption: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    endIcon: PropTypes.node,
    handleChange: PropTypes.func.isRequired,
    handleOpen: PropTypes.func,
    label: PropTypes.node.isRequired,
    multi: PropTypes.bool,
    options: PropTypes.arrayOf(filterButtonOptionType).isRequired,
    showArrowIcon: PropTypes.bool,
    showClearIcon: PropTypes.bool,
    showClearOption: PropTypes.bool,
    startIcon: PropTypes.node,
    toggle: PropTypes.bool,
    autocomplete: PropTypes.bool,
    isOpen: PropTypes.bool,
    menuProps: PropTypes.shape(),
    debounceMs: PropTypes.number,
    disabled: PropTypes.bool,
};

FilterButton.defaultProps = {
    className: null,
    defaultEmptyOption: null,
    defaultOption: null,
    handleOpen: null,
    endIcon: null,
    multi: false,
    showArrowIcon: true,
    showClearIcon: false,
    showClearOption: false,
    startIcon: null,
    toggle: false,
    autocomplete: false,
    isOpen: false,
    menuProps: {},
    debounceMs: 1000,
    disabled: false,
};

const Forwarded = React.forwardRef((props, ref) => <FilterButton {...props} ref={ref} />);

export default React.memo(Forwarded);
